import { apiClient } from './apiClient'
import type { MailDeliveryMode } from './account'
import type { UserOrganization } from './auth'

export interface AdminUser {
  id: number
  email: string
  name: string
  organization: UserOrganization
  isActive: boolean
  passwordReady: boolean
  isAdmin: boolean
  createdAt: string | null
  lastLoginAt: string | null
  lastActivityAt: string | null
}

export type AdminOrganization = UserOrganization & {
  createdAt: string | null
}

import type { WatchlistData } from './watchlist'
import type { WatchlistItem } from './watchlist'

export interface AdminUserActivity {
  id: number
  kind: string
  summary: string
  createdAt: string
  meta: Record<string, unknown> | null
}

export interface AdminUserInvite {
  purpose: 'invite' | 'reset'
  createdAt: string
  usedAt: string | null
  expiresAt: string
}

export type AdminUserWatchlist = WatchlistData & {
  listCount: number
  itemCount: number
}

export interface AdminWatchlistListSummary {
  id: number
  name: string
}

export interface AdminWatchlistListView {
  id: number | null
  name: string
  sortOrder: number
  createdAt: string | null
  items: WatchlistItem[]
  isUncategorized?: boolean
}

export interface AdminWatchlistListDetail {
  user: AdminUser
  list: AdminWatchlistListView
  allLists: AdminWatchlistListSummary[]
}

export interface AdminUserDetail {
  user: AdminUser
  watchlist: AdminUserWatchlist
  invites: AdminUserInvite[]
  activity: AdminUserActivity[]
}

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  meta?: { mailDelivery?: MailDeliveryMode }
}

function parseError(err: unknown, fallback: string): Error {
  const ax = err as { response?: { data?: { error?: string } }; message?: string }
  return new Error(ax.response?.data?.error || ax.message || fallback)
}

export async function fetchAdminOrganizations(): Promise<AdminOrganization[]> {
  try {
    const res = await apiClient.get<ApiResponse<AdminOrganization[]>>('/admin/organizations')
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke hente organisationer')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke hente organisationer')
  }
}

export async function createAdminOrganization(name: string): Promise<AdminOrganization> {
  try {
    const res = await apiClient.post<ApiResponse<AdminOrganization>>('/admin/organizations', {
      name,
    })
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke oprette organisation')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke oprette organisation')
  }
}

export async function fetchAdminUsers(): Promise<AdminUser[]> {
  try {
    const res = await apiClient.get<ApiResponse<AdminUser[]>>('/admin/users')
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke hente brugere')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke hente brugere')
  }
}

export async function fetchAdminUserDetail(userId: number): Promise<AdminUserDetail> {
  try {
    const res = await apiClient.get<ApiResponse<AdminUserDetail>>(`/admin/users/${userId}`)
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke hente bruger')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke hente bruger')
  }
}

export async function createAdminUser(
  email: string,
  name: string,
  organizationId: number
): Promise<{ user: AdminUser; mailDelivery: MailDeliveryMode }> {
  try {
    const res = await apiClient.post<ApiResponse<AdminUser>>('/admin/users', {
      email,
      name,
      organizationId,
    })
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke oprette bruger')
    }
    return {
      user: res.data.data,
      mailDelivery: res.data.meta?.mailDelivery ?? 'log',
    }
  } catch (e) {
    throw parseError(e, 'Kunne ikke oprette bruger')
  }
}

export async function updateAdminUser(
  userId: number,
  fields: {
    name?: string
    email?: string
    organizationId?: number
    isActive?: boolean
  }
): Promise<AdminUser> {
  try {
    const res = await apiClient.patch<ApiResponse<AdminUser>>(`/admin/users/${userId}`, fields)
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke opdatere bruger')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke opdatere bruger')
  }
}

export async function setAdminUserOrganization(
  userId: number,
  organizationId: number
): Promise<AdminUser> {
  return updateAdminUser(userId, { organizationId })
}

export async function setAdminUserActive(
  userId: number,
  isActive: boolean
): Promise<AdminUser> {
  return updateAdminUser(userId, { isActive })
}

export async function deleteAdminUser(
  userId: number,
  confirmEmail: string
): Promise<void> {
  try {
    await apiClient.delete(`/admin/users/${userId}`, {
      data: { confirmEmail },
    })
  } catch (e) {
    throw parseError(e, 'Kunne ikke slette bruger')
  }
}

export async function resendAdminInvite(
  userId: number
): Promise<{ user: AdminUser; mailDelivery: MailDeliveryMode }> {
  try {
    const res = await apiClient.post<ApiResponse<AdminUser>>(
      `/admin/users/${userId}/invite`
    )
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke sende invitation')
    }
    return {
      user: res.data.data,
      mailDelivery: res.data.meta?.mailDelivery ?? 'log',
    }
  } catch (e) {
    throw parseError(e, 'Kunne ikke sende invitation')
  }
}

export async function fetchAdminWatchlistListDetail(
  userId: number,
  listId: number
): Promise<AdminWatchlistListDetail> {
  try {
    const res = await apiClient.get<ApiResponse<AdminWatchlistListDetail>>(
      `/admin/users/${userId}/watchlist/lists/${listId}`
    )
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke hente liste')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke hente liste')
  }
}

export async function fetchAdminWatchlistUncategorized(
  userId: number
): Promise<AdminWatchlistListDetail> {
  try {
    const res = await apiClient.get<ApiResponse<AdminWatchlistListDetail>>(
      `/admin/users/${userId}/watchlist/uncategorized`
    )
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke hente watchlist')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke hente watchlist')
  }
}

export async function renameAdminWatchlistList(
  userId: number,
  listId: number,
  name: string
): Promise<AdminWatchlistListSummary & { sortOrder: number; createdAt: string }> {
  try {
    const res = await apiClient.patch<ApiResponse<AdminWatchlistListSummary & { sortOrder: number; createdAt: string }>>(
      `/admin/users/${userId}/watchlist/lists/${listId}`,
      { name }
    )
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke omdøbe liste')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke omdøbe liste')
  }
}

export async function deleteAdminWatchlistList(userId: number, listId: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/users/${userId}/watchlist/lists/${listId}`)
  } catch (e) {
    throw parseError(e, 'Kunne ikke slette liste')
  }
}

export async function updateAdminWatchlistItemLists(
  userId: number,
  itemId: number,
  listIds: number[]
): Promise<WatchlistItem> {
  try {
    const res = await apiClient.patch<ApiResponse<WatchlistItem>>(
      `/admin/users/${userId}/watchlist/items/${itemId}`,
      { listIds }
    )
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke opdatere emne')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke opdatere emne')
  }
}

export async function deleteAdminWatchlistItem(userId: number, itemId: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/users/${userId}/watchlist/items/${itemId}`)
  } catch (e) {
    throw parseError(e, 'Kunne ikke fjerne emne')
  }
}
